/**
 * OnboardingProvider — per-user onboarding state in Firestore
 * (Hugo_Onboarding/{uid}) + the first-run flow + funnel analytics.
 *
 * Responsibilities:
 *  - Load/create the user's onboarding doc (migrating any old localStorage
 *    checklist state from the P0 WelcomeChecklist).
 *  - FIRST LOGIN: auto-start the "Find the fire" tour — aha moment first,
 *    checklist after (opens when that tour ends).
 *  - Listen for real app events (emitOnboardingStep) and mark checklist
 *    steps done when the user actually performs them.
 *  - Log the funnel to Hugo_ActivityLog: onboarding_first_login,
 *    tour_started/completed/skipped, onboarding_step_completed,
 *    aha_moment_reached (with elapsed seconds).
 *
 * Resilience rule: every Firestore write is best-effort — onboarding must
 * never block or break app usage (state also lives in memory for the session).
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { doc, onSnapshot, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDemoRole } from '../contexts/DemoRoleContext';
import { useTour } from '../tours/TourProvider';
import { logActivity } from '../utils/firestore';
import {
  ONBOARDING_STEP_EVENT,
  TOUR_EVENT,
  ONBOARDING_STEPS,
  OPEN_WELCOME_EVENT,
} from './onboardingEvents';

const OnboardingContext = createContext(null);

export function useOnboarding() {
  return (
    useContext(OnboardingContext) || {
      steps: {},
      markStep: () => {},
      ready: false,
    }
  );
}

const legacyKey = (uid) => `repsit_welcome_${uid}`;

function readLegacyState(uid) {
  try {
    const raw = window.localStorage.getItem(legacyKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const { canViewAs } = useDemoRole();
  const { startTour } = useTour();
  const uid = user?.uid || null;
  // The getting-started arc (auto-tour, checklist) is the ORG ADMIN journey —
  // scoped members (FLS, DH, respondents…) skip it; their role experience is
  // their onboarding. canViewAs ≙ org admin of the active org.
  const isOrgAdmin = canViewAs;

  const [state, setState] = useState(null); // Firestore doc data (or memory fallback)
  const [ready, setReady] = useState(false);
  const stateRef = useRef(null);
  stateRef.current = state;
  const initializedRef = useRef(false);

  // ── Capture the user's timezone (best-effort, every login) ──────────────
  // The morning-digest workflow (WF9) delivers at 6 AM in each recipient's
  // local timezone; this is where it learns what that timezone is.
  useEffect(() => {
    if (!uid) return;
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) {
        setDoc(doc(db, 'Hugo_UserPreferences', uid), { timezone }, { merge: true }).catch(() => {});
      }
    } catch {
      /* timezone stays unset → digest defaults to America/New_York */
    }
  }, [uid]);

  // ── Load / create the onboarding doc ────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setState(null);
      setReady(false);
      initializedRef.current = false;
      return undefined;
    }

    const ref = doc(db, 'Hugo_Onboarding', uid);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setState(snap.data());
          setReady(true);
          initializedRef.current = true;
          return;
        }
        if (initializedRef.current) return; // creation in flight
        initializedRef.current = true;

        // First login for this user (in this system): migrate any legacy
        // localStorage checklist progress, create the doc, start the flow.
        const legacy = readLegacyState(uid);
        const migratedSteps = legacy?.steps || {};
        const isBrandNew = !legacy;
        const initial = {
          steps: migratedSteps,
          firstLoginAt: Timestamp.now(),
          ahaMomentAt: null,
          welcomeSeen: Boolean(legacy?.seen),
          createdAt: Timestamp.now(),
        };
        setState(initial); // memory fallback even if the write fails
        setReady(true);
        setDoc(ref, initial).catch((err) =>
          console.warn('Onboarding doc create failed (continuing in-memory):', err.message)
        );
        logActivity(uid, 'onboarding_first_login', 'drift-accumulation', {});

        if (isBrandNew && isOrgAdmin) {
          // Aha moment first: launch the Find-the-fire tour directly.
          // Small delay lets the dashboard render its targets.
          setTimeout(() => startTour('find-the-fire'), 900);
        }
      },
      (err) => {
        console.warn('Onboarding state unavailable (continuing without):', err.message);
        setState({ steps: {} });
        setReady(true);
      }
    );
    return unsubscribe;
  }, [uid, startTour]);

  // ── Persist helper (best-effort) ─────────────────────────────────────────
  const persist = useCallback(
    (patch) => {
      if (!uid) return;
      updateDoc(doc(db, 'Hugo_Onboarding', uid), patch).catch((err) =>
        console.warn('Onboarding update failed (state kept in memory):', err.message)
      );
    },
    [uid]
  );

  // ── Mark a step complete (from real app events or manually) ─────────────
  const markStep = useCallback(
    (step) => {
      if (!uid || !ONBOARDING_STEPS.includes(step)) return;
      const current = stateRef.current || { steps: {} };
      if (current.steps?.[step]) return; // already done

      const nextSteps = { ...(current.steps || {}), [step]: true };
      const patch = { [`steps.${step}`]: true };
      const next = { ...current, steps: nextSteps };

      // Aha moment = they found the fire (first red site), first time only.
      if (step === 'find-the-fire' && !current.ahaMomentAt) {
        const at = Timestamp.now();
        next.ahaMomentAt = at;
        patch.ahaMomentAt = at;
        const startedAt = current.firstLoginAt?.toMillis?.() || Date.now();
        logActivity(uid, 'aha_moment_reached', 'drift-accumulation', {
          elapsedSec: Math.round((Date.now() - startedAt) / 1000),
        });
      }

      setState(next);
      persist(patch);
      logActivity(uid, 'onboarding_step_completed', 'drift-accumulation', { step });
    },
    [uid, persist]
  );

  // ── Real-action events → step completion ────────────────────────────────
  useEffect(() => {
    const handler = (e) => markStep(e.detail?.step);
    window.addEventListener(ONBOARDING_STEP_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_STEP_EVENT, handler);
  }, [markStep]);

  // ── Tour lifecycle → analytics + open checklist after the first tour ────
  useEffect(() => {
    const handler = (e) => {
      const { type, key, stepIndex } = e.detail || {};
      if (!uid || !type || !key) return;
      logActivity(uid, `tour_${type}`, 'drift-accumulation', {
        tour: key,
        ...(stepIndex != null ? { stepIndex } : {}),
      });

      // Finishing a tour (FINISH on its last popup) completes that checklist
      // step. Tim's Beta-1 review: the survey and import steps never showed as
      // complete after FINISH. Doing the real thing (filter to a site, submit a
      // survey, add a member, approve an import) still completes it too.
      if (type === 'completed' && ONBOARDING_STEPS.includes(key)) {
        markStep(key);
      }

      // When the first-login tour ends (finished or skipped), show the
      // checklist once so they can see the rest of the path.
      if ((type === 'completed' || type === 'skipped') && key === 'find-the-fire') {
        const current = stateRef.current || {};
        if (!current.welcomeSeen) {
          setState((s) => ({ ...(s || {}), welcomeSeen: true }));
          persist({ welcomeSeen: true });
          setTimeout(() => window.dispatchEvent(new Event(OPEN_WELCOME_EVENT)), 400);
        }
      }
    };
    window.addEventListener(TOUR_EVENT, handler);
    return () => window.removeEventListener(TOUR_EVENT, handler);
  }, [uid, persist, markStep]);

  const value = useMemo(
    () => ({
      steps: state?.steps || {},
      ahaMomentAt: state?.ahaMomentAt || null,
      markStep,
      ready,
    }),
    [state, markStep, ready]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
